import React, { useRef, useState } from 'react';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { ExtendedTableLowCode } from '@/components/shared/table/ExtendedTableLowCode';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import { Button, CustomCellProperties, Dialog, ModalRef } from 'alurkerja-ui';
import { useParams } from 'react-router-dom';
import { IFilterSpec } from '@/components/shared/filter/TableFilterPanel';
import { TestCaseService } from '@/api/testCase';
import moment from 'moment';
import { useBackNavigate } from '@/utils/hooks/useBackNavigate';
import { HideCustomCreateButton } from '@/components/shared/button/HideCustomButtonCreate';
import { downloadFile } from '@/utils/libs/fileUtil';

export const ListArchiveTestCase: React.FC = () => {
  const { id } = useParams();
  const backNavigate = useBackNavigate();
  const ref = useRef<ModalRef>(null);

  const spec = useCustomSpec('/api/crud/test-case', {
    columns: [
      { key: 'case_code', label: 'Case Code' },
      { key: 'title', label: 'Title' },
      { key: 'total_step', label: 'Step' },
      { key: 'created_at', label: 'Created Date' },
    ],
  });

  const filterSpec: IFilterSpec[] = [
    { name: 'case_code', label: 'Case Code', type: 'text' },
    { name: 'title', label: 'Title', type: 'text' },
  ];

  const customCell = ({ name, value, defaultCell }: CustomCellProperties) => {
    if (name === 'created_at') {
      return <>{moment(value).format('DD MMMM YYYY')}</>;
    }
    return defaultCell;
  };

  const [selectedRow, setSelectedRow] = useState<string[]>([]);
  const { mutate: unArchiveMutate, isPending: isUnarchiveLoading } =
    TestCaseService.useUnarchive();
  const { mutate: exportMutate, isPending: isExportArchiveLoading } = TestCaseService.useExportArchive();

  const unarchiveAction = (idList: string[]) => {
    idList.forEach(id => {
      unArchiveMutate(id, {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Test Case has been Unarchived',
            callback: () => {
              ref.current?.closeModal();
              backNavigate();
            },
          });
        },
        onError: () => {
          Dialog.error({
            title: 'Error',
            description: 'Failed to Unarchive Test Case',
          });
        },
      });
    });
  };

  const exportAction = (id: string) => {
    exportMutate(id, {
      onSuccess: response => {
        Dialog.success({
          title: 'Success',
          description: 'Test case has been exported',
        });

        downloadFile(response, `TestCasesArchived.xlsx`);
      },
      onError: () => {
        Dialog.error({
          title: 'Error',
          description: 'Failed to export test case',
        });
      },
    });
  };

  return (
    <div className="tab-group">
      <div className="justify-between tab-list">
        <OutlineButton onClick={backNavigate}>Back</OutlineButton>

        <div>
          <Button
            onClick={() => unarchiveAction(selectedRow)}
            loading={isUnarchiveLoading || isExportArchiveLoading}
            disabled={selectedRow.length === 0}
            className={`mr-2`}
          >
            Revert Back
          </Button>

          <Button
            onClick={() => {
              if (id) {
                exportAction(id);
              }
            }}
            loading={isUnarchiveLoading || isExportArchiveLoading}
            disabled={isUnarchiveLoading}
          >
            Export
          </Button>
        </div>
      </div>

      <div className="mx-4">
        <ExtendedTableLowCode
          spec={spec}
          defaultFilter={{ project_id: id, is_archive: true }}
          hideActionColumn
          customButtonCreate={HideCustomCreateButton}
          customCell={customCell}
          showFilter
          filterSpec={filterSpec}
          showCheckbox
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
        />
      </div>
    </div>
  );
};
